/**
 * Why money cannot be booked at this workstation, as the backend names it
 * (`backend/src/domain/cash/cashBookingRefusal.ts`): every booking it refuses answers 409 with
 * one of these `code`s, and `GET /cash-drawer/booking-readiness` answers `{ ready: false, code }`
 * before the operator starts. The English `message` beside the code is for the logs; the operator
 * reads the translation of the code.
 */
export type CashBookingRefusalCode =
    | 'NO_CASH_POINT'
    | 'NOT_CASH_REGISTER'
    | 'NO_OPEN_CASH_SESSION'
    | 'CASH_SESSION_CLOSED';

/** The i18n key of each refusal. */
export const CASH_BOOKING_REFUSAL_KEYS: Readonly<Record<CashBookingRefusalCode, string>> = {
    NO_CASH_POINT: 'cashBooking.refusal.noCashPoint',
    NOT_CASH_REGISTER: 'cashBooking.refusal.notCashRegister',
    NO_OPEN_CASH_SESSION: 'cashBooking.refusal.noOpenCashSession',
    CASH_SESSION_CLOSED: 'cashBooking.refusal.cashSessionClosed',
};

/** The heading shown above a refusal on a page that books money. */
export const CASH_BOOKING_REFUSAL_TITLE_KEY = 'cashBooking.refusal.title';

/** `GET /cash-drawer/booking-readiness` */
export interface CashBookingReadiness {
    ready: boolean;
    code?: string;
    message?: string;
    sessionNumber?: number;
}

const isRefusalCode = (value: unknown): value is CashBookingRefusalCode =>
    typeof value === 'string' && Object.keys(CASH_BOOKING_REFUSAL_KEYS).includes(value);

/**
 * The i18n key of the cash booking refusal a response body carries - an error body or a
 * readiness answer - or `null` when it carries none.
 */
export function cashBookingRefusalKey(body: unknown): string | null {
    const code = typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : null;
    return isRefusalCode(code) ? CASH_BOOKING_REFUSAL_KEYS[code] : null;
}
